import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { ReactElement } from "react";
import {
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/data-table";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { SampleSetNumber } from "@/shared/components/domain/sample-set/sample-set-number";
import { Procedure } from "@/services/backend/procedures/procedure/procedure";
import { sampleTypes } from "@/shared/components/enum-labels";
import { H4 } from "@/shared/components/typography";
import { useListProceduresQuery } from "@/services/backend/procedures/procedure/service";
import { CollapsibleDataTable } from "@/shared/components/collapsible-data-table";
import MouldResult from "@/shared/components/domain/moulds/mould-result";
import { ProcedureType, SampleType } from "@/services/backend/types";
import { MouldResultTable } from "@/shared/components/domain/differentiation/mould-result-table";
import DifferentiationModal from "@/shared/components/domain/differentiation/differentiation-modal";
import { checkIfNotEvaluable } from "@/shared/components/domain/moulds/mould-count";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";

const createTableColumns = (
  differentiationId: string,
  differentiationDisabled: boolean,
): ColumnDef<Procedure>[] => [
  {
    id: "sampleSet",
    accessorKey: "procedure",
    header: "Probenset",
    cell: ({ row }) => {
      const procedureData = row.original;
      return <SampleSetNumber sampleSetId={procedureData.sampleSetId} />;
    },
  },
  {
    id: "sampleType",
    accessorKey: "sampleType",
    header: "Probentyp",
    cell: ({ row }) => {
      const procedureData = row.original;
      return <div>{sampleTypes[procedureData.sampleType]}</div>;
    },
  },
  {
    id: "mouldResult",
    accessorKey: "id",
    header: "Schimmelpilze / Hefen",
    cell: ({ row }) => {
      const procedureData = row.original;
      return (
        <div className="flex items-center space-x-2">
          <MouldResult procedure={procedureData} />
        </div>
      );
    },
  },
  {
    id: "actions",
    accessorKey: "id",
    header: "Aktionen",
    cell: ({ row }) => {
      const procedureData = row.original;
      return (
        <div className="flex items-center gap-2">
          <DifferentiationModal
            disabled={
              differentiationDisabled || checkIfNotEvaluable(procedureData)
            }
            procedure={procedureData}
            differentiationId={differentiationId}
            sampleSetId={procedureData.sampleSetId}
          />
        </div>
      );
    },
  },
];

interface DifferentiationTableProps {
  differentiationId: string;
  ownerId: string;
  ownerType: string;
  procedureType: ProcedureType;
  sampleType: SampleType;
  label: string;
  visibilityState?: VisibilityState;
  immutable?: boolean;
}

function DifferentiationTable({
  differentiationId,
  ownerId,
  ownerType,
  procedureType,
  sampleType,
  label,
  visibilityState = {},
  immutable,
}: DifferentiationTableProps): ReactElement {
  const {
    data: list,
    error,
    isLoading,
    isFetching,
  } = useListProceduresQuery(
    {
      ownerId: {
        active: true,
        values: [ownerId],
      },
      ownerType: {
        active: true,
        values: [ownerType],
      },
      procedureType: {
        active: true,
        values: [procedureType],
      },
      sampleType: {
        active: true,
        values: [sampleType],
      },
      limit: 0,
      offset: 0,
    },
    { refetchOnMountOrArgChange: true },
  );

  const canCreate = useFeatureAuth("inspections.differentiation.create");
  const cols = createTableColumns(differentiationId, !canCreate || !!immutable);
  const table = useDataTableController(
    cols,
    list?.items || [],
    visibilityState,
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || isFetching) {
    return (
      <DataTableContainer className="w-full">
        <CollapsibleDataTable
          table={table}
          columns={cols}
          collapsibleTriggerId="sampleSet"
          renderCollapsibleContent={() => null}
          isLoading
        />
      </DataTableContainer>
    );
  }

  if (list != null && list.items != null && list.items.length > 0) {
    return (
      <div className="flex w-full flex-col space-y-4">
        <H4>{label}</H4>
        <DataTableContainer className="w-full">
          <CollapsibleDataTable
            table={table}
            columns={cols}
            collapsibleTriggerId="sampleSet"
            renderCollapsibleContent={(procedure: Procedure) => (
              <MouldResultTable procedure={procedure} />
            )}
          />
        </DataTableContainer>
      </div>
    );
  }

  return <div />;
}

export default DifferentiationTable;
